import { useState } from 'react';
import { Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import { MethodPresetCard } from '../../components/onboarding/MethodPresetCard';
import { SettingsSubPageLayout } from '../../components/settings/SettingsSubPageLayout';
import { Button } from '../../components/ui/Button';
import { budgetsAPI } from '../../data/budgets';
import pt from '../../locales/pt';

async function budgetMethodLoader() {
  const [presetsRes, activeRes] = await Promise.all([
    budgetsAPI.getPresets(),
    budgetsAPI.getActiveMethod(),
  ]);
  return {
    presets: presetsRes.success ? presetsRes.data : [],
    activeMethod: activeRes.success ? activeRes.data : null,
  };
}

export function BudgetMethodSettings() {
  const { presets, activeMethod } = useLoaderData();
  const revalidator = useRevalidator();
  const [selected, setSelected] = useState(
    () => activeMethod?.preset_id || presets[0]?.id || null,
  );
  const [loading, setLoading] = useState(false);

  const onSave = async () => {
    const preset = presets.find((p) => p.id === selected);
    if (!preset) return;
    setLoading(true);
    const response = await budgetsAPI.createMethodFromPreset({
      preset_id: selected,
      customisations: preset.groups.map((group) => ({
        name: group.name,
        color: group.color,
        icon: group.icon,
        target_percentage: group.target_percentage,
      })),
    });
    setLoading(false);
    if (!response.success) {
      toast.error(typeof response.message === 'string' ? response.message : pt.budget.method.changeError);
      return;
    }
    revalidator.revalidate();
    toast.success(pt.settings.budgetMethod.saved);
  };

  return (
    <SettingsSubPageLayout
      section={pt.settings.budgetMethod.section}
      title={pt.settings.budgetMethod.title}
      subtitle={pt.settings.budgetMethod.subtitle}
      footer={
        <Button type="button" className="w-full" disabled={loading || !selected} onClick={onSave}>
          {pt.settings.budgetMethod.submit}
        </Button>
      }
    >
      <div className="flex gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-900 dark:bg-amber-950/30">
        <Warning className="mt-0.5 h-4 w-4 shrink-0 text-amber-800 dark:text-amber-300" aria-hidden />
        <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
          {pt.settings.budgetMethod.warning}
        </p>
      </div>

      <div className="space-y-3">
        {presets.map((preset) => (
          <MethodPresetCard
            key={preset.id}
            preset={preset}
            selected={selected === preset.id}
            onSelect={setSelected}
            recommended={preset.id === 'fifty_thirty_twenty'}
          />
        ))}
      </div>
    </SettingsSubPageLayout>
  );
}

BudgetMethodSettings.loader = budgetMethodLoader;
